import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { trackEvent } from "@/lib/engagement";
import { streamSSE } from "@/lib/sse-client";
import type { IndexBuildStartResponse, IndexStatusResponse } from "@/types/paper";

export function useRagIndex(conceptId: string) {
  const queryClient = useQueryClient();
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildProgress, setBuildProgress] = useState("");
  const [buildError, setBuildError] = useState<string | null>(null);

  const { data: indexStatus, isLoading: statusLoading } =
    useQuery<IndexStatusResponse>({
      queryKey: ["rag-index", conceptId],
      queryFn: () =>
        apiClient
          .get(`api/concepts/${conceptId}/chat/index/status`)
          .json<IndexStatusResponse>(),
    });

  const buildIndex = useCallback(
    async (paperIds?: string[]) => {
      setIsBuilding(true);
      setBuildProgress("Starting index build...");
      setBuildError(null);

      try {
        const body: Record<string, unknown> = {};
        if (paperIds) body.paper_ids = paperIds;

        const res = await apiClient
          .post(`api/concepts/${conceptId}/chat/index/build`, { json: body })
          .json<IndexBuildStartResponse>();

        // Stream progress via SSE
        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:8000";
        const token = await getAuthToken();
        const streamUrl = `${baseUrl}/api/concepts/${conceptId}/chat/index/stream/${res.job_id}`;

        let receivedTerminal = false;

        for await (const event of streamSSE(streamUrl, {
          Authorization: `Bearer ${token}`,
        })) {
          if (event.event === "progress") {
            const data = JSON.parse(event.data);
            setBuildProgress(data.message);
          } else if (event.event === "complete") {
            const data = JSON.parse(event.data);
            const msg = `Index built: ${data.papers_indexed} papers, ${data.chunks_created} chunks`;
            setBuildProgress(msg);
            setIsBuilding(false);
            toast.success("Chat index built", { description: msg });
            trackEvent("index_built", {
              concept_id: conceptId,
              papers_indexed: data.papers_indexed,
              chunks_created: data.chunks_created,
            });
            receivedTerminal = true;
            // Refresh index status
            await queryClient.invalidateQueries({
              queryKey: ["rag-index", conceptId],
            });
            return;
          } else if (event.event === "error") {
            const data = JSON.parse(event.data);
            setBuildError(data.error);
            setIsBuilding(false);
            toast.error("Index build failed", { description: data.error });
            receivedTerminal = true;
            return;
          }
        }

        // Stream closed without a terminal event (backend race condition).
        // Check the actual index status to determine outcome.
        if (!receivedTerminal) {
          setIsBuilding(false);
          const freshStatus = await apiClient
            .get(`api/concepts/${conceptId}/chat/index/status`)
            .json<IndexStatusResponse>();
          await queryClient.invalidateQueries({
            queryKey: ["rag-index", conceptId],
          });
          if (freshStatus.is_indexed) {
            toast.success("Chat index built");
          } else {
            toast.error("Index build may have failed — check status");
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Index build failed";
        setBuildError(msg);
        setIsBuilding(false);
        toast.error("Index build failed", { description: msg });
      }
    },
    [conceptId, queryClient],
  );

  const clearIndex = useCallback(async () => {
    try {
      await apiClient.delete(`api/concepts/${conceptId}/chat/index`);
      await queryClient.invalidateQueries({
        queryKey: ["rag-index", conceptId],
      });
      // Backend also resets include_in_chat — refresh papers to reflect
      await queryClient.invalidateQueries({
        queryKey: ["papers", conceptId],
      });
      toast.success("Index cleared");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to clear index";
      setBuildError(msg);
      toast.error("Failed to clear index", { description: msg });
    }
  }, [conceptId, queryClient]);

  return {
    indexStatus,
    statusLoading,
    isBuilding,
    buildProgress,
    buildError,
    buildIndex,
    clearIndex,
  };
}
